import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { BREAKPOINT_EXTRA_LARGE_PX, BREAKPOINT_LARGE_PX } from '@/settings';
import {
  baseStyles,
  formStyles,
  skewedLabelStyles,
  typographyStyles,
} from '@/styles';
import { RowData } from '@/types';

@customElement('fg-table')
export class FgTable extends LitElement {
  @property({ type: String })
  tableLabel?: string;

  @property({ type: Array })
  columnHeadings: string[] = [];

  @property({ type: Array })
  columnRows?: RowData | null;

  @property({ type: Boolean })
  small = false;

  @property({ type: Boolean })
  forceShowScrollbar = false;

  render() {
    if (!this.columnRows || !this.columnRows.length) {
      return nothing;
    }

    return html`
      <div
        class="generic-table ${classMap({
          small: this.small,
          'force-show-scrollbar': this.forceShowScrollbar,
        })}"
      >
        <table>
          <thead>
            <tr>
              ${this.tableLabel
                ? html`
                    <th>${this.tableLabel}</th>
                  `
                : nothing}
              ${this.columnHeadings?.map(
                columnHeading => html`
                  <th>${columnHeading}</th>
                `,
              )}
            </tr>
          </thead>
          <tbody>
            ${this.columnRows?.map(
              columnRow => html`
                <tr>
                  <td>${columnRow.rowName}</td>
                  ${columnRow.rowValues?.map(
                    rowValue => html`
                      <td>
                        ${rowValue.subValue
                          ? html`
                              <div class="sub-value">${rowValue.subValue}</div>
                            `
                          : nothing}
                        ${rowValue.mainValue}
                      </td>
                    `,
                  )}
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    formStyles,
    skewedLabelStyles,
    css`
      .generic-table:not(.small) {
        display: block;
        height: max-content;
        margin-left: -1rem;
        margin-right: -1rem;
        padding-bottom: 1rem;
        overflow-x: auto;
      }

      .generic-table table {
        max-width: 100%;
        padding-inline: 1rem;
        width: 100%;
      }

      .generic-table.small table {
        padding-inline: 0;
      }

      .generic-table th,
      .generic-table td {
        min-width: 130px;
        font-size: 1rem;
        padding: 10px 1rem;
        text-align: right;
      }

      .generic-table.small th,
      .generic-table.small td {
        min-width: 75px;
        padding: 4px 8px;
        line-height: 160%;
        border-right: 1px solid var(--fg-gray--100);
        border-bottom: 1px solid var(--fg-gray--100);
      }

      .generic-table.small td {
        background-color: #fff;
      }

      .generic-table.small th:first-child,
      .generic-table.small td:first-child {
        border-left: 1px solid var(--fg-gray--100);
      }

      .generic-table th {
        background-color: var(--fg-primary, var(--fg-fallback-primary));
        color: #fff;
        font-weight: 600;
      }

      .generic-table td:first-child {
        font-weight: 500;
      }

      .generic-table .sub-value {
        font-weight: 400;
        font-size: 0.875rem;
        text-decoration: line-through;
      }

      .generic-table.small .sub-value {
        font-size: 1rem;
      }

      .generic-table th,
      .generic-table td {
        border-right: 1px solid var(--fg-gray--300);
        border-bottom: 1px solid var(--fg-gray--300);
      }

      .generic-table th:first-child,
      .generic-table td:first-child {
        border-left: 1px solid var(--fg-gray--300);
        text-align: left;
      }

      .generic-table th:first-child {
        border-top-left-radius: 8px;
      }

      .generic-table th:last-child {
        border-top-right-radius: 8px;
      }

      .generic-table tr:last-child td:first-child {
        border-bottom-left-radius: 8px;
      }

      .generic-table tr:last-child td:last-child {
        border-bottom-right-radius: 8px;
      }

      .force-show-scrollbar::-webkit-scrollbar {
        -webkit-appearance: none;
        height: 7px;
      }

      .force-show-scrollbar::-webkit-scrollbar-thumb {
        border-radius: 2px;
        background-color: rgba(0, 0, 0, 0.5);
        box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        .generic-table table {
          width: auto;
        }

        .generic-table.small table {
          width: 100%;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .generic-table th,
        .generic-table td {
          width: 170px;
        }

        .generic-table.small th,
        .generic-table.small td {
          width: auto;
        }
      }
    `,
  ];
}
